/**
 * useAppView.ts — T-649 top-level view discriminator (map ↔ admin) for the router-less OptiYield SPA.
 *
 * WHAT IT DOES. The authed app has two top-level views now: the map (MapShell) and the admin page
 * (AdminPage). Which one shows rides in the URL hash — `#admin[/section]` for admin, anything else
 * (a map hash, or empty) for the map — reusing the T-237 technique (own History, encode state in the
 * hash, parse on boot, restore on popstate) rather than adding a router. See adminHash.ts for why the
 * admin and map hashes are provably disjoint.
 *
 * WHY IT IS NOT A SECOND HISTORY OWNER. useMapUrlState raw-owns window.history for the map (the
 * camera hash + the T-071/T-081 leave-guard sentinel), and its own header notes that two popstate
 * owners fight. This hook stays clear of that channel two ways: (1) it WRITES history only on an
 * explicit view navigation (into admin, between sections, out to the map), never from its popstate
 * handler; (2) its popstate handler only ACTS on a view/section CHANGE, so a map→map Back derives
 * view='map' unchanged and no-ops. Mount-exclusivity does the rest: AdminPage and MapShell are never
 * mounted together, so useMapUrlState's listener is simply absent whenever the admin view is up.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  parseAdminHash,
  encodeAdminHash,
  DEFAULT_ADMIN_SECTION,
  type AdminSection,
} from '../lib/adminHash'

export type AppView = 'map' | 'admin'

export interface UseAppView {
  /** Which top-level view is showing. */
  view: AppView
  /** The active admin section (meaningful only when view === 'admin'). */
  section: AdminSection
  /** Switch to the admin page. pushState so a Back returns to the map the user came from. */
  navigateToAdmin: (section?: AdminSection) => void
  /** Switch admin sections. replaceState so the URL stays deep-linkable without stacking Back entries. */
  selectSection: (section: AdminSection) => void
  /** Leave the admin page for the map. */
  navigateToMap: () => void
}

function deriveFromHash(hash: string): { view: AppView; section: AdminSection } {
  const admin = parseAdminHash(hash)
  return admin
    ? { view: 'admin', section: admin.section }
    : { view: 'map', section: DEFAULT_ADMIN_SECTION }
}

export function useAppView(): UseAppView {
  const [state, setState] = useState(() => deriveFromHash(window.location.hash))
  // Did we enter admin from within the app (a map entry sits behind the pushed #admin)? Governs
  // whether "Back to map" can history.back() to it, or must synthesise a map URL because admin was
  // the cold boot entry (a shared #admin deep-link / a hand-typed one).
  const enteredFromAppRef = useRef(false)

  useEffect(() => {
    const onPopState = () => {
      const next = deriveFromHash(window.location.hash)
      // Act only on a genuine change. A map→map Back derives view='map' unchanged → this no-ops and
      // writes nothing, leaving useMapUrlState the sole owner of the map's History channel.
      setState((prev) => (prev.view === next.view && prev.section === next.section ? prev : next))
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  const navigateToAdmin = useCallback((section: AdminSection = DEFAULT_ADMIN_SECTION) => {
    enteredFromAppRef.current = true
    window.history.pushState(null, '', encodeAdminHash(section))
    setState({ view: 'admin', section })
  }, [])

  const selectSection = useCallback((section: AdminSection) => {
    window.history.replaceState(null, '', encodeAdminHash(section))
    setState((prev) => (prev.section === section ? prev : { ...prev, section }))
  }, [])

  const navigateToMap = useCallback(() => {
    if (enteredFromAppRef.current) {
      enteredFromAppRef.current = false
      // Pop the #admin entry → land on the map entry behind it. popstate then switches the view and
      // MapShell remounts + re-hydrates from that hash (useMapUrlState.initial).
      window.history.back()
    } else {
      // Cold deep-link into admin: nothing good sits behind. Strip the hash so the map opens on its
      // default camera, and switch the view directly.
      window.history.replaceState(null, '', window.location.pathname + window.location.search)
      setState({ view: 'map', section: DEFAULT_ADMIN_SECTION })
    }
  }, [])

  return {
    view: state.view,
    section: state.section,
    navigateToAdmin,
    selectSection,
    navigateToMap,
  }
}
